import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Cpu, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { stagger, fadeUp } from "@/components/common/Reveal";
import { MACHINES } from "@/data/content";

const FILTERS = ["All", "FANUC", "Siemens"];

const Machines = () => {
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? MACHINES : MACHINES.filter((m) => m.tags.includes(filter));

  return (
    <div>
      <PageHeader
        eyebrow="Machine Marketplace"
        title="Fully reconditioned machines, ready to perform"
        subtitle="Every machine is stripped, rebuilt, calibrated and tested — backed by warranty and FANUC/Siemens control expertise."
        image="https://images.unsplash.com/photo-1655393001768-d946c97d6fd1?auto=format&fit=crop&w=1400&q=80"
      />

      <section className="py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3 mb-12">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                className={`px-5 py-2.5 rounded-full text-sm border transition-all ${filter === f ? "bg-[#0055FF] border-[#0055FF] text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                {f}
              </button>
            ))}
          </div>

          <motion.div key={filter} variants={stagger} initial="hidden" animate="show" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((m) => (
              <motion.div key={m.id} variants={fadeUp}>
                <Link to={`/machines/${m.id}`} className="group block rounded-2xl overflow-hidden border border-slate-200 card-hover h-full" data-testid={`machine-card-${m.id}`}>
                  <div className="relative h-60 overflow-hidden">
                    <img src={m.image} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs glass border-slate-300">{m.condition}</span>
                    <span className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs bg-[#0055FF] text-white">{m.controller.split(" ")[0]}</span>
                  </div>
                  <div className="p-6">
                    <span className="text-xs uppercase tracking-widest text-dim">{m.type}</span>
                    <h3 className="mt-2 text-xl leading-tight">{m.name}</h3>
                    <div className="mt-4 flex items-center gap-2 text-sm text-slate-600"><Cpu className="w-4 h-4 text-[#0055FF]" /> {m.controller}</div>
                    <div className="mt-5 flex items-center justify-between pt-4 border-t border-slate-200">
                      <span className="text-sm text-dim">Year {m.year}</span>
                      <span className="inline-flex items-center gap-1 text-sm text-[#0055FF] font-medium">View details <ArrowRight className="w-4 h-4" /></span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 border-t border-slate-200 bg-white text-center">
        <p className="text-dim flex items-center justify-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#0055FF]" /> Can't find what you need? We source and recondition machines to order.</p>
        <Link to="/enquiry" className="btn-primary mt-6">Send an Enquiry <ArrowRight className="w-4 h-4" /></Link>
      </section>
    </div>
  );
};

export default Machines;
